export const DIRECT_GALLERY_DEFAULT_QUEUED_BYTES = 24 * 1024 * 1024;
export const DIRECT_GALLERY_MAX_QUEUED_BYTES = DIRECT_GALLERY_DEFAULT_QUEUED_BYTES;
export const DIRECT_GALLERY_MAX_REQUEST_BYTES = 32 * 1024 * 1024;

function finiteNonNegative(value, field) {
  if (value === null || value === undefined || value === '') {
    throw new TypeError(`${field} is required`);
  }
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number < 0) {
    throw new TypeError(`${field} must be a non-negative safe integer`);
  }
  return number;
}

export function isDirectGallerySupported({
  isNative,
  androidVersion,
  capability
} = {}) {
  return isNative === true
    && Number(androidVersion) >= 29
    && capability?.supported === true;
}

export function normalizeDirectGallerySession(value = {}) {
  if (!value.sessionId || !value.token) {
    throw new TypeError('Direct Gallery session ID and token are required');
  }
  if (String(value.token).length < 43) {
    throw new TypeError('Direct Gallery token is too short');
  }

  let url;
  try {
    url = new URL(value.uploadUrl);
  } catch {
    throw new TypeError('Direct Gallery upload URL is invalid');
  }
  if (url.protocol !== 'http:' || url.hostname !== '127.0.0.1') {
    throw new TypeError('Direct Gallery upload URL must be loopback-only');
  }
  const expectedPath = `/v1/session/${encodeURIComponent(String(value.sessionId))}/chunk`;
  if (url.pathname !== expectedPath || url.username || url.password || url.search || url.hash) {
    throw new TypeError('Direct Gallery upload URL does not match the session');
  }

  const maxQueuedBytes = value.maxQueuedBytes === undefined
    ? DIRECT_GALLERY_DEFAULT_QUEUED_BYTES
    : finiteNonNegative(value.maxQueuedBytes, 'maxQueuedBytes');
  if (maxQueuedBytes <= 0 || maxQueuedBytes > DIRECT_GALLERY_MAX_QUEUED_BYTES) {
    throw new RangeError('Direct Gallery queue limit is invalid');
  }

  return {
    sessionId: String(value.sessionId),
    uploadUrl: url.toString(),
    token: String(value.token),
    nextOffset: finiteNonNegative(value.nextOffset ?? 0, 'nextOffset'),
    maxQueuedBytes
  };
}

export function validateDirectGalleryAck({
  expectedOffset,
  chunkBytes,
  ack
} = {}) {
  const offset = finiteNonNegative(expectedOffset, 'expectedOffset');
  const bytes = finiteNonNegative(chunkBytes, 'chunkBytes');
  const wanted = offset + bytes;
  if (!Number.isSafeInteger(wanted)
      || Number(ack?.committedBytes) !== bytes
      || Number(ack?.nextOffset) !== wanted) {
    throw new Error('Direct Gallery acknowledgement offset mismatch');
  }
  return wanted;
}

function positionedAckInteger(ack, field) {
  const value = ack?.[field];
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error('Direct Gallery positioned acknowledgement mismatch');
  }
  return value;
}

export function buildPositionedWriteRequest(sessionValue, {
  data,
  position
} = {}) {
  const session = normalizeDirectGallerySession(sessionValue);
  if (!(data instanceof Uint8Array)) {
    throw new TypeError('Direct Gallery positioned data must be a Uint8Array');
  }
  if (data.byteLength <= 0 || data.byteLength > DIRECT_GALLERY_MAX_REQUEST_BYTES) {
    throw new RangeError('Direct Gallery positioned request size is invalid');
  }
  if (!Number.isSafeInteger(position) || position < 0
      || position > Number.MAX_SAFE_INTEGER - data.byteLength) {
    throw new TypeError('Direct Gallery positioned write position is invalid');
  }

  const url = new URL(session.uploadUrl);
  url.pathname = `/v1/session/${encodeURIComponent(session.sessionId)}/range`;
  return {
    method: 'PUT',
    url: url.toString(),
    headers: {
      Authorization: `Bearer ${session.token}`,
      'Content-Type': 'application/octet-stream',
      'X-Recording-Position': String(position)
    },
    body: data
  };
}

export function validatePositionedWriteAck({
  position,
  dataBytes,
  ack
} = {}) {
  const expectedPosition = finiteNonNegative(position, 'position');
  const expectedCommittedBytes = finiteNonNegative(dataBytes, 'dataBytes');
  const expectedEnd = expectedPosition + expectedCommittedBytes;
  if (!Number.isSafeInteger(expectedEnd)) {
    throw new TypeError('Positioned write range is invalid');
  }

  const committedBytes = positionedAckInteger(ack, 'committedBytes');
  const acknowledgedPosition = positionedAckInteger(ack, 'position');
  const extentBytes = positionedAckInteger(ack, 'extentBytes');
  if (committedBytes !== expectedCommittedBytes
      || acknowledgedPosition !== expectedPosition
      || extentBytes < expectedEnd) {
    throw new Error('Direct Gallery positioned acknowledgement mismatch');
  }
  return { committedBytes, position: acknowledgedPosition, extentBytes };
}
